import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { PageLayout } from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPhotoUrl } from "@/utils/photoUrl";
import {
  FileSpreadsheet, FileText, Download, Users, User, Search, Filter, CheckSquare, Square,
} from "lucide-react";

const POSITIONS = ["Ponteiro", "Central", "Oposto", "Levantador", "Líbero"];

export default function PlayerReportPage({ evaluator, onLogout }) {
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const fetchPlayers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/players`);
      setPlayers(res.data || []);
    } catch (error) {
      toast.error("Erro ao carregar jogadores");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const getName = (p) => p.first_name ? `${p.first_name} ${p.last_name}` : p.name || "";

  const filtered = useMemo(() => {
    return players.filter((p) => {
      if (positionFilter !== "all" && p.position !== positionFilter) return false;
      if (typeFilter !== "all" && (p.member_type || "membro") !== typeFilter) return false;
      if (search.trim()) {
        const name = getName(p).toLowerCase();
        if (!name.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [players, positionFilter, typeFilter, search]);

  const togglePlayer = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((p) => p.id)));
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const ids = selected.size > 0 ? Array.from(selected).join(",") : "";
      const url = `${API}/players/report/${format}${ids ? `?player_ids=${ids}` : ""}`;
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `jogadores.${format === "excel" ? "xlsx" : "pdf"}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Relatorio ${format.toUpperCase()} baixado!`);
    } catch (error) {
      toast.error(`Erro ao gerar ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <PageLayout evaluator={evaluator} onLogout={onLogout} title="Relatorio de Jogadores">
      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">
          {selected.size > 0
            ? `${selected.size} jogador(es) selecionado(s)`
            : `${filtered.length} jogadores (todos serao exportados)`}
        </p>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => handleDownload("excel")}
            disabled={!!downloading}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="download-excel-btn"
          >
            {downloading === "excel" ? (
              <span className="animate-spin mr-2">&#9696;</span>
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            Excel
          </Button>
          <Button
            onClick={() => handleDownload("pdf")}
            disabled={!!downloading}
            variant="destructive"
            data-testid="download-pdf-btn"
          >
            {downloading === "pdf" ? (
              <span className="animate-spin mr-2">&#9696;</span>
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 border-border/50 mb-4">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar jogador..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-muted/50 h-9"
                data-testid="search-player-input"
              />
            </div>
            <Select value={positionFilter} onValueChange={setPositionFilter}>
              <SelectTrigger className="w-36 h-9 bg-muted/50" data-testid="position-filter">
                <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Posicoes</SelectItem>
                {POSITIONS.map((pos) => (
                  <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-32 h-9 bg-muted/50" data-testid="type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="membro">Membros</SelectItem>
                <SelectItem value="convidado">Convidados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Player List */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader className="py-3 px-4 border-b border-border/50">
          <div className="flex items-center justify-between">
            <button
              onClick={selectAll}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="select-all-btn"
            >
              {allSelected ? (
                <CheckSquare className="h-4 w-4 text-primary" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              {allSelected ? "Desmarcar todos" : "Selecionar todos"}
            </button>
            <span className="text-xs text-muted-foreground">{filtered.length} jogadores</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nenhum jogador encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {filtered.map((player) => (
                <div
                  key={player.id}
                  onClick={() => togglePlayer(player.id)}
                  role="button"
                  tabIndex={0}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                    selected.has(player.id) ? "bg-primary/10" : "hover:bg-muted/40"
                  }`}
                  data-testid={`player-row-${player.id}`}
                >
                  <Checkbox
                    checked={selected.has(player.id)}
                    className="pointer-events-none flex-shrink-0"
                  />
                  <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0">
                    {player.photo_url ? (
                      <img src={getPhotoUrl(player.photo_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getName(player)}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px] h-4">{player.position}</Badge>
                      {player.yankee && (
                        <Badge className="text-[10px] h-4 bg-primary/20 text-primary border-0">{player.yankee}</Badge>
                      )}
                      <Badge className={`text-[10px] h-4 px-1.5 ${
                        (player.member_type || "membro") === "membro"
                          ? "bg-emerald-500/20 text-emerald-400 border-0"
                          : "bg-amber-500/20 text-amber-400 border-0"
                      }`}>
                        {(player.member_type || "membro") === "membro" ? "Membro" : "Convidado"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
